import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProfileCard } from "@/pages/account/Overview";
import { StatusBadge } from "@/components/brand";
import { PasswordInput } from "@/pages/Login";

const TABS = [["personal", "Personal Info"], ["security", "Security"], ["verification", "Verification"]];

const PersonalTab = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ full_name: user.full_name, phone: user.phone || "" });
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.patch("/me/profile", f); setUser(data); toast.success("Profile updated"); } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <form onSubmit={save} className="grid max-w-2xl gap-5 sm:grid-cols-2">
      <div><label className="label-cr">Full name</label><input data-testid="profile-name-input" className="input-cr" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></div>
      <div><label className="label-cr">Mobile number</label><input data-testid="profile-phone-input" className="input-cr" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
      <div><label className="label-cr">Email</label><input disabled className="input-cr opacity-60" value={user.email} /></div>
      <div><label className="label-cr">Username</label><input disabled className="input-cr opacity-60" value={user.username} /></div>
      <div><label className="label-cr">Country</label><input disabled className="input-cr opacity-60" value={user.country} /></div>
      <div><label className="label-cr">Date of birth</label><input disabled className="input-cr opacity-60" value={user.dob} /></div>
      <button data-testid="profile-save-btn" className="btn-gold sm:col-span-2 sm:w-fit">Save changes</button>
    </form>
  );
};

const SecurityTab = () => {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [link, setLink] = useState("");
  const change = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); } catch (e2) { toast.error(apiError(e2)); }
  };
  const verify = async () => {
    try { const { data } = await api.post("/auth/verify-email/request"); setLink(data.sandbox_link || ""); toast.success("Verification link sent"); } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="grid max-w-3xl gap-8 md:grid-cols-2">
      <form onSubmit={change} className="card-cr space-y-4 p-5">
        <p className="font-semibold">Change password</p>
        <PasswordInput testid="current-password-input" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
        <PasswordInput testid="new-password-input" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
        <button data-testid="change-password-btn" className="btn-gold w-full">Update password</button>
      </form>
      <div className="card-cr space-y-3 p-5">
        <p className="font-semibold">Email verification</p>
        <p className="text-sm">Status: <StatusBadge status={user.email_verified ? "verified" : "unverified"} testid="email-verified-status" /></p>
        {!user.email_verified && <button data-testid="send-verification-btn" onClick={verify} className="btn-outline w-full">Send verification email</button>}
        {link && <p data-testid="sandbox-verify-link" className="text-xs text-cr-muted">Email delivery is MOCKED in preview. Open: <a href={link} className="text-cr-gold underline">{link.slice(0, 40)}…</a></p>}
      </div>
    </div>
  );
};

const VerificationTab = () => {
  const { user, reloadUser } = useAuth();
  const [f, setF] = useState({ document_type: "pan", document_last4: "", address: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/me/verification", f); await reloadUser(); toast.success("Verification submitted"); } catch (e2) { toast.error(apiError(e2)); }
  };
  if (["pending", "verified"].includes(user.kyc_status))
    return <div data-testid="kyc-status-panel" className="card-cr max-w-xl p-6"><p>Identity verification: <StatusBadge status={user.kyc_status} /></p><p className="mt-2 text-sm text-cr-muted">{user.kyc_status === "pending" ? "Our team is reviewing your submission." : "You are verified and eligible to withdraw."}</p></div>;
  return (
    <form onSubmit={submit} data-testid="kyc-form" className="card-cr grid max-w-xl gap-4 p-6">
      <p className="text-sm text-cr-muted">Identity verification is required before withdrawals. Submit your document details and our team will review them.</p>
      {user.kyc_status === "rejected" && <p className="text-sm text-cr-danger">Your previous submission was rejected. Please resubmit.</p>}
      <div><label className="label-cr">Document type</label>
        <select data-testid="kyc-doc-type" className="input-cr" value={f.document_type} onChange={(e) => setF({ ...f, document_type: e.target.value })}>
          {[["pan", "PAN card"], ["aadhaar", "Aadhaar"], ["passport", "Passport"], ["driving_license", "Driving licence"], ["national_id", "National ID"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select></div>
      <div><label className="label-cr">Last 4 characters of document number</label><input data-testid="kyc-last4-input" maxLength={4} className="input-cr" value={f.document_last4} onChange={(e) => setF({ ...f, document_last4: e.target.value })} /></div>
      <div><label className="label-cr">Residential address</label><input data-testid="kyc-address-input" className="input-cr" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
      <button data-testid="kyc-submit-btn" className="btn-gold">Submit for review</button>
    </form>
  );
};

export default function Profile() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "personal";
  return (
    <div data-testid="profile-page" className="space-y-6">
      <div><h1 className="text-2xl font-bold">My Profile</h1><p className="text-sm text-cr-ink/70">Manage your personal information and account settings</p></div>
      <ProfileCard user={user} />
      <div className="flex gap-6 overflow-x-auto border-b border-cr-line">
        {TABS.map(([k, l]) => <button key={k} data-testid={`profile-tab-${k}`} onClick={() => setSp({ tab: k })} className={`-mb-px shrink-0 border-b-2 pb-3 text-sm ${tab === k ? "border-cr-gold text-cr-gold" : "border-transparent text-cr-ink/75"}`}>{l}</button>)}
      </div>
      {tab === "personal" && <PersonalTab />}
      {tab === "security" && <SecurityTab />}
      {tab === "verification" && <VerificationTab />}
    </div>
  );
}
